import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { usePopperTooltip } from "react-popper-tooltip";

const CustomTooltip = ({
  disabled = false,
  className,
  children,
  content,
  maxWidth,
  place,
  tooltipClassName,
  isVisible = false,
  stopPropagation = false,
}: any) => {
  const triggerElementRef = useRef<HTMLButtonElement | null>(null);
  const tooltipElementRef = useRef<HTMLDivElement | null>(null);
  const [isCoarsePointer, setIsCoarsePointer] = useState(
    () =>
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches
  );

  const {
    getArrowProps,
    getTooltipProps,
    setTooltipRef,
    setTriggerRef,
    visible,
    update,
  } = usePopperTooltip({
    offset: [0, 10],
    placement: place,
    trigger: isCoarsePointer ? "click" : "hover",
  });

  const setTriggerElement = useCallback(
    (node: HTMLButtonElement | null) => {
      triggerElementRef.current = node;
      setTriggerRef(node);
    },
    [setTriggerRef]
  );

  const setTooltipElement = useCallback(
    (node: HTMLDivElement | null) => {
      tooltipElementRef.current = node;
      setTooltipRef(node);
    },
    [setTooltipRef]
  );

  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return;
    }

    const coarsePointerQuery = window.matchMedia("(pointer: coarse)");
    const handlePointerChange = (event: MediaQueryListEvent) => {
      setIsCoarsePointer(event.matches);
    };

    setIsCoarsePointer(coarsePointerQuery.matches);

    if (coarsePointerQuery.addEventListener) {
      coarsePointerQuery.addEventListener("change", handlePointerChange);
    } else {
      coarsePointerQuery.addListener(handlePointerChange);
    }

    return () => {
      if (coarsePointerQuery.removeEventListener) {
        coarsePointerQuery.removeEventListener("change", handlePointerChange);
      } else {
        coarsePointerQuery.removeListener(handlePointerChange);
      }
    };
  }, []);

  useEffect(() => {
    if ((visible || isVisible) && update) {
      update();
    }
  }, [visible, update, isVisible]);

  useEffect(() => {
    if (!(visible || isVisible) || !update) {
      return;
    }

    const runUpdate = () => {
      update();
    };

    const rafId = window.requestAnimationFrame(runUpdate);
    const handleViewportChange = () => runUpdate();

    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(runUpdate)
        : null;

    if (resizeObserver) {
      if (triggerElementRef.current) {
        resizeObserver.observe(triggerElementRef.current);
      }
      if (tooltipElementRef.current) {
        resizeObserver.observe(tooltipElementRef.current);
      }
      resizeObserver.observe(document.body);
    }

    const mutationObserver =
      typeof MutationObserver !== "undefined"
        ? new MutationObserver(runUpdate)
        : null;

    if (mutationObserver) {
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    }

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [visible, isVisible, update]);

  const handleTriggerClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (stopPropagation || isCoarsePointer) {
      event.stopPropagation();
    }
    if (isCoarsePointer) {
      event.currentTarget.blur();
    }
  };

  if (disabled) {
    return <>{children}</>;
  }

  return (
    <>
      <button
        className={className}
        style={{
          appearance: "none",
          background: "transparent",
          border: "none",
          height: "fit-content",
          outline: "none",
          padding: 0,
          pointerEvents: disabled ? "none" : "auto",
          WebkitTapHighlightColor: "transparent",
        }}
        type="button"
        ref={setTriggerElement}
        onClick={handleTriggerClick}
      >
        {children}
      </button>

      {/* Portalled to <body>, deliberately outside .portal-shell. Popper
          measures the trigger with getBoundingClientRect() — real screen px —
          and positions with a transform, which resolves in the positioned
          element's own coordinate space. Rendered inside the scaled shell those
          two spaces disagree and the tooltip lands ~33px off its icon.
          Compensating inside a Popper modifier did not work: Popper v2 already
          applies its own partial scale normalisation, so dividing by the zoom
          over-corrected (33px error became 46px). Anchoring the tooltip in the
          unscaled body puts measurement and placement back in one space, which
          needs no arithmetic at all. The tooltip therefore renders 1:1 rather
          than at --app-zoom. */}
      {(visible || isVisible) &&
        ReactDOM.createPortal(
          <div
            ref={setTooltipElement}
            className={`!max-w-[${maxWidth}]`}
            style={{
              maxWidth,
              width: "fit-content",
              boxShadow: "0px 10px 100px rgba(60, 66, 87, 0.2)",
              zIndex: "99999999",
            }}
            {...(getTooltipProps({
              className: `dcdial-portal-tooltip-container ${
                tooltipClassName || ""
              }`,
            }) as any)}
          >
            <div dangerouslySetInnerHTML={{ __html: content }} />
            <div {...getArrowProps({ className: "tooltip-arrow" })} />
          </div>,
          document.body
        )}
    </>
  );
};

export default CustomTooltip;
